import { useCallback, useEffect, useRef, useState } from 'react';
import { listenSafe } from '../utils/safeTauriEvent';
import { invoke } from '@tauri-apps/api/core';
import { MONACO_LAYOUT_FLUSH_EVENT } from '../components/ui/ResizableSplitter';
import { TERMINAL_LAYOUT_SETTLED_EVENT } from '../components/terminal/terminalLayoutSettle';
import { useTerminalIsDark } from './useTerminalIsDark';
import { usePreferencesStore } from '../store/preferencesStore';
import { withMacEditKeys } from '../utils/terminalMacKeys';
import { loadTerminalAddons } from '../utils/terminalAddons';
import { isMacPlatform } from '../utils/keyboard';
import { useTerminalOptionAsMeta } from './useTerminalOptionAsMeta';
import {
  buildTerminalTheme,
  terminalXtermOptions,
  clampTerminalFontSize,
} from '../utils/terminalTheme';


/**
 * xterm.js bound to a Rust-side SSH PTY (ssh_term.rs). Mirror of
 * useEmbeddedTerminal, but the session is opened by vault connection id —
 * credentials are resolved inside Rust and never reach this hook.
 *
 * Also listens for `ssh-term-ai-exec`: whenever the AI runs a command on this
 * tab's connection (over its separate exec channel), the command and its
 * output are echoed here so the user always sees what the AI did.
 *
 * **Attach mode** (`attach.termId`): the session already exists because RUST
 * opened it — an approved MCP command runs in a tab of its own, and the command
 * is sent from a worker thread the instant the user approves, so this hook can
 * never be first. It therefore skips `ssh_term_open`, adopts the id it is
 * given, and replays the output that landed before this xterm existed.
 *
 * KNOWN LIMIT (phase 2): if that session drops, Rust opens a replacement with a
 * NEW id. This hook adopts an id once, so the tab must be closed and reopened
 * to follow it; re-attaching in place means reworking the teardown below.
 *
 * @param {string} connId - vault sftp item id
 * @param {boolean} mounted - Terminal panel is open
 * @param {boolean} visible - this tab is active
 * @param {(err?: string) => void} onExit - session ended (server close/error)
 * @param {{ termId?: string, ai?: boolean }} [attach] - adopt a Rust-owned
 *   session instead of opening one; `ai` also closes the tab to typing
 * @param {{ focused?: boolean, keyFilter?: (e: KeyboardEvent) => boolean,
 *   onStatus?: (status: string) => void, onInput?: (data: string) => void }} [opts]
 *   - pane integration: only the `focused` pane takes DOM focus on show;
 *   `keyFilter` returning false keeps a key (a pane shortcut) away from the
 *   remote shell; `onStatus('running')` once connected; `onInput` sees what
 *   the user typed (broadcast fan-out — never called for the AI tab)
 * @returns {{ termRef: React.Ref, connecting: boolean, write: (data: string) => void }}
 *   - `connecting` is true until the SSH session is up (the view overlays a
 *   spinner meanwhile)
 */
export function useSshTerminal(connId, mounted, visible, onExit, attach, opts = {}) {
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const attachTermId = attach?.termId || null;
  const isAiTab = !!attach?.ai;
  // The terminal's own light/dark (Settings → Extensions → Terminal → Theme).
  const isDark = useTerminalIsDark();
  const isDarkRef = useRef(isDark);
  isDarkRef.current = isDark;
  const terminalFontSize = usePreferencesStore((s) => s.terminalFontSize);
  const fontSizeRef = useRef(terminalFontSize);
  fontSizeRef.current = terminalFontSize;
  const termRef = useRef(null);
  const xtermRef = useRef(null);
  const termIdRef = useRef(null);
  const fitAddonRef = useRef(null);
  const addonsRef = useRef({});
  const commandDoneRef = useRef(null);
  const unlistenersRef = useRef([]);
  const initStartedRef = useRef(false);
  const refitRef = useRef(() => {});
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;
  const [connecting, setConnecting] = useState(true);
  const optionAsMetaRef = useTerminalOptionAsMeta(xtermRef, connecting);

  useEffect(() => {
    if (!mounted || !visible || initStartedRef.current) return undefined;

    let disposed = false;
    initStartedRef.current = true;

    async function setup() {
      const [{ Terminal }, { FitAddon }] = await Promise.all([
        import('@xterm/xterm'),
        import('@xterm/addon-fit'),
      ]);
      await import('@xterm/xterm/css/xterm.css');

      if (disposed || !termRef.current) return;

      const term = new Terminal(
        terminalXtermOptions(isDarkRef.current, fontSizeRef.current, optionAsMetaRef.current)
      );

      // Same Warp-style block dividers as local terminals: if the remote
      // shell emits OSC 133;A prompt markers (shell integration), render a
      // real DOM divider at each block boundary, skipping the first prompt.
      // Tracked so a remote `clear` can dispose them (see below).
      const promptSeen = { current: false };
      const dividers = [];
      term.parser.registerOscHandler(133, (data) => {
        if (typeof data === 'string' && data.startsWith('A')) {
          if (promptSeen.current) {
            const marker = term.registerMarker(0);
            if (marker) {
              const deco = term.registerDecoration({ marker });
              deco?.onRender((el) => {
                if (!el.dataset.apbDivider) {
                  el.dataset.apbDivider = '1';
                  el.classList.add('term-block-divider');
                }
              });
              dividers.push({ marker, deco });
            }
          } else {
            promptSeen.current = true;
          }
        }
        return true;
      });

      const fitAddon = new FitAddon();
      term.loadAddon(fitAddon);
      term.open(termRef.current);
      addonsRef.current = await loadTerminalAddons(term);
      addonsRef.current.prompts?.onCommandDone((e) => commandDoneRef.current?.(e));
      if (disposed) {
        term.dispose();
        return;
      }
      fitAddon.fit();
      term.attachCustomKeyEventHandler?.(
        withMacEditKeys(
          (e) => (optsRef.current.keyFilter ? optsRef.current.keyFilter(e) : true),
          (seq) => term.input(seq),
          isMacPlatform()
        )
      );

      xtermRef.current = term;
      fitAddonRef.current = fitAddon;

      unlistenersRef.current.push(
        await listenSafe('ssh-term-output', (event) => {
          const payload = event.payload;
          if (payload?.id === termIdRef.current && payload?.data) {
            // A screen clear wipes the text — drop the divider decorations
            // with it and let the next prompt start a fresh "first block".
            if (payload.data.includes('\x1b[2J') || payload.data.includes('\x1b[3J')) {
              for (const d of dividers) {
                d.deco?.dispose?.();
                d.marker?.dispose?.();
              }
              dividers.length = 0;
              promptSeen.current = false;
            }
            term.write(payload.data);
          }
        })
      );

      unlistenersRef.current.push(
        await listenSafe('ssh-term-exit', (event) => {
          const payload = event.payload;
          if (payload?.id === termIdRef.current) {
            term.writeln(
              payload?.error
                ? `\r\n\x1b[31m[Connection lost: ${payload.error}]\x1b[0m`
                : '\r\n\x1b[33m[Session closed]\x1b[0m'
            );
            onExitRef.current?.(payload?.error);
          }
        })
      );

      // AI exec mirror: same connection, any tab state — print the command
      // and its output as a visible audit trail.
      unlistenersRef.current.push(
        await listenSafe('ssh-term-ai-exec', (event) => {
          const p = event.payload;
          if (p?.connId !== connId) return;
          const line = (s) => String(s || '').replace(/\r?\n/g, '\r\n');
          term.writeln(`\r\n\x1b[36m[AI] $ ${line(p.command)}\x1b[0m`);
          if (p.stdout) term.write(`${line(p.stdout)}\r\n`);
          if (p.stderr) term.write(`\x1b[31m${line(p.stderr)}\x1b[0m\r\n`);
          term.writeln(`\x1b[36m[AI] exit ${p.exitCode}\x1b[0m`);
        })
      );

      try {
        if (attachTermId) {
          // Attach mode. The replay comes FIRST and is written before anything
          // else: the command was sent before this xterm existed, so without it
          // the user watches an empty tab and concludes nothing ran.
          const replay = await invoke('ssh_term_attach', { termId: attachTermId });
          if (disposed) return;
          // Not `ssh_term_close` on dispose, unlike below: this session belongs
          // to Rust and may still be running an approved command. Closing the
          // tab must not kill a command the user approved.
          termIdRef.current = attachTermId;
          if (replay) term.write(replay);
          setConnecting(false);
        } else {
          const { termId } = await invoke('ssh_term_open', {
            connId,
            cols: term.cols,
            rows: term.rows,
          });
          if (disposed) {
            invoke('ssh_term_close', { termId }).catch(() => {});
            return;
          }
          termIdRef.current = termId;
          setConnecting(false);
        }
        optsRef.current.onStatus?.('running');
        if (optsRef.current.focused !== false) term.focus();
      } catch (err) {
        setConnecting(false);
        term.writeln(`\r\n\x1b[31m[Connect failed: ${String(err?.message || err)}]\x1b[0m`);
        onExitRef.current?.(String(err?.message || err));
        return;
      }

      term.onData((data) => {
        const tid = termIdRef.current;
        if (!tid) return;
        // An AI tab takes approved commands and Ctrl-C, nothing else. Rust
        // refuses the rest anyway (`ssh_term_write`); dropping it here as well
        // is what stops every keystroke becoming a failed round trip, and keeps
        // the user's own shell state — `cd`, `su` — out of a tab whose next
        // approval card claims to say exactly what will run.
        if (isAiTab && data !== '\x03') return;
        invoke('ssh_term_write', { termId: tid, data }).catch(() => {});
        if (!isAiTab) optsRef.current.onInput?.(data);
      });

      term.onResize(({ cols, rows }) => {
        const tid = termIdRef.current;
        if (!tid) return;
        invoke('ssh_term_resize', { termId: tid, cols, rows }).catch(() => {});
      });
    }

    setup().catch((err) => {
      console.error('[useSshTerminal] setup failed:', err);
      initStartedRef.current = false;
    });

    return () => {
      disposed = true;
    };
    // `attachTermId` and `isAiTab` are deliberately NOT dependencies. They are
    // the tab's identity — a tab is an AI attach tab or it is not, for life —
    // and this effect is a one-shot behind `initStartedRef`. A re-run would set
    // `disposed` on a setup still in flight (which then closes the PTY it just
    // opened) while the new run stops at the guard: a dead terminal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, visible, connId]);

  useEffect(() => {
    const term = xtermRef.current;
    if (!term) return;
    term.options.theme = buildTerminalTheme(isDark);
  }, [isDark]);

  // Live font-size preference (Settings → Extensions → Terminal).
  useEffect(() => {
    const term = xtermRef.current;
    if (!term) return;
    const size = clampTerminalFontSize(terminalFontSize);
    if (term.options.fontSize === size) return;
    term.options.fontSize = size;
    try {
      const before = `${term.cols}x${term.rows}`;
      fitAddonRef.current?.fit();
      const tid = termIdRef.current;
      if (tid && `${term.cols}x${term.rows}` !== before) {
        invoke('ssh_term_resize', { termId: tid, cols: term.cols, rows: term.rows }).catch(
          () => {}
        );
      }
    } catch {
      // ignore
    }
  }, [terminalFontSize]);

  // Refit when the tab becomes visible or the layout resizes.
  useEffect(() => {
    if (!visible || !fitAddonRef.current || !xtermRef.current) return undefined;

    const refit = () => {
      try {
        // Hidden panel (display:none while another panel is shown): the
        // container measures 0×0 and fit() would derive a garbage grid.
        if (!termRef.current || termRef.current.clientWidth === 0) return;
        const term = xtermRef.current;
        const before = term ? `${term.cols}x${term.rows}` : '';
        fitAddonRef.current?.fit();
        const tid = termIdRef.current;
        // Same-size resizes are no-ops server-side — skip the round trip.
        if (term && tid && `${term.cols}x${term.rows}` !== before) {
          invoke('ssh_term_resize', { termId: tid, cols: term.cols, rows: term.rows }).catch(
            () => {}
          );
        }
      } catch {
        // ignore
      }
    };

    refitRef.current = refit;
    // Focus so the user can type the moment the tab is shown — the tab's
    // focused pane only.
    if (optsRef.current.focused !== false) xtermRef.current?.focus();
    const timer = setTimeout(refit, 80);
    window.addEventListener('resize', refit);
    window.addEventListener(MONACO_LAYOUT_FLUSH_EVENT, refit);
    // Layout settled (split, close, zoom, preset, drag end) — see terminalLayoutSettle.js.
    window.addEventListener(TERMINAL_LAYOUT_SETTLED_EVENT, refit);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', refit);
      window.removeEventListener(MONACO_LAYOUT_FLUSH_EVENT, refit);
      window.removeEventListener(TERMINAL_LAYOUT_SETTLED_EVENT, refit);
    };
  }, [visible]);

  // Container ResizeObserver — same rationale as useEmbeddedTerminal: layout
  // changes (devtools footer/panel, splitters) alter the container without a
  // window resize; without this the grid keeps stale rows and the scrollbar
  // desyncs. The refit no-ops while hidden or same-size.
  useEffect(() => {
    const el = termRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => refitRef.current());
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  // Teardown on unmount: close the Rust session and dispose xterm.
  useEffect(() => {
    return () => {
      const tid = termIdRef.current;
      if (tid) {
        invoke('ssh_term_close', { termId: tid }).catch(() => {});
        termIdRef.current = null;
      }
      unlistenersRef.current.forEach((u) => u?.());
      unlistenersRef.current = [];
      xtermRef.current?.dispose();
      xtermRef.current = null;
      fitAddonRef.current = null;
      initStartedRef.current = false;
    };
  }, []);

  // Focus follows the pane focus while shown.
  const focused = opts.focused !== false;
  useEffect(() => {
    if (visible && focused) xtermRef.current?.focus();
  }, [visible, focused]);

  const write = useCallback(
    (data) => {
      const tid = termIdRef.current;
      if (!tid || isAiTab) return;
      invoke('ssh_term_write', { termId: tid, data }).catch(() => {});
    },
    [isAiTab]
  );

  // The pane's search bar and copy tools reach the xterm through these.
  const getTerm = useCallback(() => xtermRef.current, []);
  const getSearch = useCallback(() => addonsRef.current.search || null, []);
  const getPrompts = useCallback(() => addonsRef.current.prompts?.lines() || [], []);
  const getLink = useCallback(() => addonsRef.current.links?.hovered() || null, []);
  // Finished commands (card 5) go to whoever set this — the pane's notifier.
  const setOnCommandDone = useCallback((fn) => {
    commandDoneRef.current = fn;
  }, []);
  return { termRef, connecting, write, getTerm, getSearch, getPrompts, getLink, setOnCommandDone };
}
